import React, {useState} from "react";
import classNames from "classnames";
import {observer} from "mobx-react-lite";
import {useAppState} from "@stores/AppStateContext";

import {getShortMonthName, WEEK_DAYS_SHORT} from "../../../../utils/date";
import {ChevronLeftIcon} from "../../icons/ChevronLeftIcon";
import {ChevronRightIcon} from "../../icons/ChevronRightIcon";

import "./CalendarSelect.css";

export interface CalendarSelectOptionsProps {
    selectedDate?: Date;
    onChange: (date: Date) => void;
    mode?: "day" | "month" | "year";
}

export const CalendarSelectOptions = observer(
    ({
         selectedDate,
         onChange,
         mode = "month",
     }: CalendarSelectOptionsProps
    ) => {
        const appState = useAppState();
        const actualDate = selectedDate ?? new Date();
        const [selectedYear, setSelectedYear] = useState(actualDate.getFullYear());
        const [selectedMonth, setSelectedMonth] = useState(actualDate.getMonth());
        const [yearRangeStart, setYearRangeStart] = useState(Math.floor(actualDate.getFullYear() / 12) * 12);

        const months = Array.from({length: 12}, (_, i) => i);
        const years = Array.from({length: 12}, (_, i) => yearRangeStart + i);
        const [currentMode, setCurrentMode] = useState<"year" | "month" | "day">(mode);

        const handleYearSelect = (year: number) => {
            if (mode === "year") {
                const newDate = new Date(year, 0, 1);
                onChange(newDate);
                appState.closeContextMenusByType('calendar-select-options');
                return;
            }
            setSelectedYear(year);
            setCurrentMode("month");
        };

        const handleMonthSelect = (month: number) => {
            setSelectedMonth(month);
            if (mode === "day") {
                setCurrentMode("day");
            } else {
                const newDate = new Date(selectedYear, month, actualDate.getDate());
                onChange(newDate);
                appState.closeContextMenusByType('calendar-select-options');
            }
        };

        const renderOptionsGrid = ({
                                       options,
                                       activeOption,
                                       onSelect,
                                       renderItem,
                                   }: {
            options: number[];
            activeOption?: number;
            onSelect: (value: number) => void;
            renderItem: (item: number) => React.ReactNode;
        }) => (
            <div>
                {[0, 1, 2].map(groupIndex => (
                    <div key={groupIndex} className="Calendar__Option__row">
                        {options.slice(groupIndex * 4, (groupIndex + 1) * 4).map(date => (
                            <div
                                key={date}
                                className={classNames({
                                    "title-small Calendar__Option": true,
                                    "Calendar__Option--active": activeOption === date,
                                })}
                                onClick={() => onSelect(date)}
                            >
                                {renderItem(date)}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );

        const renderDayGrid = () => {
            const dates = generateCalendarDates(selectedYear, selectedMonth);
            return (
                <>
                    <div className="Calendar__WeekDays">
                        {WEEK_DAYS_SHORT.map((day, idx) => (
                            <div key={idx} className="Calendar__WeekDay label-large-bold">
                                {day}
                            </div>
                        ))}
                    </div>
                    <div className="Calendar__Options--day">
                        {dates.map(({date, isCurrentMonth}) => (
                            <div
                                key={date.toDateString()}
                                className={classNames({
                                    "Calendar__Options--dayMode body-medium": true,
                                    "Calendar__Option--active": actualDate.toDateString() === date.toDateString(),
                                    "Calendar__Option--dimmed": !isCurrentMonth,
                                })}
                                onClick={() => {
                                    onChange(date);
                                    appState.closeContextMenusByType('calendar-select-options');
                                }}
                            >
                                {date.getDate()}
                            </div>
                        ))}
                    </div>
                </>
            );
        };

        const renderGrid = () => {
            switch (currentMode) {
                case "day":
                    return renderDayGrid();
                case "month":
                    return renderOptionsGrid({
                        options: months,
                        activeOption: selectedDate && selectedDate.getFullYear() === selectedYear ? actualDate.getMonth() : undefined,
                        onSelect: handleMonthSelect,
                        renderItem: (month) => getShortMonthName(new Date(selectedYear, month, 1)),
                    });
                case "year":
                    return renderOptionsGrid({
                        options: years,
                        activeOption: selectedYear,
                        onSelect: handleYearSelect,
                        renderItem: (year) => year,
                    });
            }
        };

        const handlePrev = () => {
            if (currentMode === "day") {
                if (selectedMonth === 0) {
                    setSelectedMonth(11);
                    setSelectedYear(selectedYear - 1);
                } else {
                    setSelectedMonth(selectedMonth - 1);
                }
            } else if (currentMode === "month") {
                setSelectedYear(selectedYear - 1);
            } else if (currentMode === "year") {
                setYearRangeStart(yearRangeStart - 12);
            }
        };

        const handleNext = () => {
            if (currentMode === "day") {
                if (selectedMonth === 11) {
                    setSelectedMonth(0);
                    setSelectedYear(selectedYear + 1);
                } else {
                    setSelectedMonth(selectedMonth + 1);
                }
            } else if (currentMode === "month") {
                setSelectedYear(selectedYear + 1);
            } else if (currentMode === "year") {
                setYearRangeStart(yearRangeStart + 12);
            }
        };

        return (
            <div className="CalendarSelectOptions">
                <div className="Calendar__ContentHeader title-small">
                    <div className="Calendar__ContentHeader__Chevron" onClick={handlePrev}>
                        <ChevronLeftIcon size={16} additionalPadding={8}/>
                    </div>
                    <div
                        className="Calendar__ContentHeader__Header"
                        onClick={() => {
                            if (mode === "year") return;
                            setCurrentMode((prev) => {
                                if (mode === "month") {
                                    return prev === "month" ? "year" : "month";
                                }
                                return prev === "day" ? "month" : prev === "month" ? "year" : "month";
                            });
                        }}
                    >
                        {currentMode === "day"
                            ? `${getShortMonthName(new Date(selectedYear, selectedMonth, 1))} ${selectedYear}`
                            : currentMode === "month"
                                ? `${selectedYear}`
                                : `${yearRangeStart} – ${yearRangeStart + 11}`}
                    </div>
                    <div className="Calendar__ContentHeader__Chevron" onClick={handleNext}>
                        <ChevronRightIcon size={16} additionalPadding={8}/>
                    </div>
                </div>
                {renderGrid()}
            </div>
        );
    });

const generateCalendarDates = (
    year: number,
    month: number
): { date: Date; isCurrentMonth: boolean }[] => {
    const firstDay = new Date(year, month, 1);
    const startDayOfWeek = firstDay.getDay();
    const startDate = new Date(firstDay);
    startDate.setDate(firstDay.getDate() - startDayOfWeek);

    const dates: { date: Date; isCurrentMonth: boolean }[] = [];

    for (let i = 0; i < 42; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        dates.push({
            date,
            isCurrentMonth: date.getMonth() === month,
        });
    }

    return dates;
};
